import { useRef } from "react";
import type React from "react";
import {
  getChatbotUsageStatus,
  streamChatbotResponse,
  type ChatbotReferenceImage,
  type ChatbotStreamStatus,
} from "../../services/chatbot.service";
import { buildRequestMessages, createMessage, createWelcomeMessage } from "./chatbotMessageUtils";
import {
  ABORT_ERROR_NAME,
  ASSISTANT_ROLE,
  EMPTY_RESPONSE_TRANSLATION_KEY,
  ERROR_MESSAGE_TRANSLATION_KEY,
  HTTP_STATUS_FORBIDDEN,
  HTTP_STATUS_TOO_MANY_REQUESTS,
  USER_ROLE,
  USAGE_LIMIT_REACHED_TRANSLATION_KEY,
} from "./chatbotHook.constants";
import { buildUsageLimitMessage } from "./chatbotUsageUtils";
import type { WidgetMessage } from "./types";

type Translate = (key: string, options?: Record<string, unknown>) => string;

interface UseChatbotStreamingOptions {
  messages: WidgetMessage[];
  sessionId: string | null;
  referenceImages: ChatbotReferenceImage[];
  isUploadingReferenceImages: boolean;
  isStreaming: boolean;
  setIsStreaming: React.Dispatch<React.SetStateAction<boolean>>;
  input: string;
  setInput: React.Dispatch<React.SetStateAction<string>>;
  setMessages: React.Dispatch<React.SetStateAction<WidgetMessage[]>>;
  setSessionId: React.Dispatch<React.SetStateAction<string | null>>;
  setReferenceImages: React.Dispatch<React.SetStateAction<ChatbotReferenceImage[]>>;
  setReferenceImageUploadError: React.Dispatch<React.SetStateAction<string | null>>;
  setIsSessionListOpen: React.Dispatch<React.SetStateAction<boolean>>;
  streamStatus: ChatbotStreamStatus;
  setStreamStatus: React.Dispatch<React.SetStateAction<ChatbotStreamStatus>>;
  loadSessions: () => Promise<void>;
  refreshSessionTurns: (targetSessionId?: string | null) => Promise<void>;
  t: Translate;
}

export interface UseChatbotStreamingReturn {
  abortControllerRef: React.RefObject<AbortController | null>;
  handleSend: (event: React.FormEvent) => void;
  handleSuggestedPrompt: (prompt: string) => void;
  handleReset: () => void;
}

export const useChatbotStreaming = ({
  messages,
  sessionId,
  referenceImages,
  isUploadingReferenceImages,
  isStreaming,
  setIsStreaming,
  input,
  setInput,
  setMessages,
  setSessionId,
  setReferenceImages,
  setReferenceImageUploadError,
  setIsSessionListOpen,
  streamStatus: _streamStatus,
  setStreamStatus,
  loadSessions,
  refreshSessionTurns,
  t,
}: UseChatbotStreamingOptions): UseChatbotStreamingReturn => {
  const abortControllerRef = useRef<AbortController | null>(null);

  const updateAssistantMessage = (messageId: string, updater: (content: string) => string): void => {
    setMessages((current) =>
      current.map((message) =>
        message.id === messageId ? { ...message, content: updater(message.content) } : message
      )
    );
  };

  const sendMessage = async (
    text: string,
    selectedReferenceImages: ChatbotReferenceImage[] = referenceImages
  ): Promise<void> => {
    const trimmed = text.trim();
    if (!trimmed || isStreaming || isUploadingReferenceImages) return;

    const userMessage = createMessage(USER_ROLE, trimmed, false, null, selectedReferenceImages);
    const requestMessages = buildRequestMessages(messages, userMessage);
    const assistantMessage = createMessage(ASSISTANT_ROLE, "");

    setMessages((current) => [...current, userMessage, assistantMessage]);
    setReferenceImages([]);
    setReferenceImageUploadError(null);
    setStreamStatus("thinking");
    setIsStreaming(true);

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const response = await streamChatbotResponse({
        messages: requestMessages,
        sessionId,
        signal: controller.signal,
        onDelta: (delta) => {
          updateAssistantMessage(assistantMessage.id, (content) => content + delta);
        },
        onStatus: setStreamStatus,
      });

      if (response.sessionId) setSessionId(response.sessionId);
      void loadSessions();
      if (response.sessionId || sessionId) {
        void refreshSessionTurns(response.sessionId || sessionId).catch(() => undefined);
      }
      if (!response.text) {
        updateAssistantMessage(assistantMessage.id, () => t(EMPTY_RESPONSE_TRANSLATION_KEY));
      }
    } catch (err) {
      const error = err as Error & { status?: number };
      if (error.name !== ABORT_ERROR_NAME) {
        if (error.status === HTTP_STATUS_FORBIDDEN) {
          updateAssistantMessage(assistantMessage.id, () =>
            error.message || "Chatbot access is disabled for this restaurant."
          );
        } else if (error.status === HTTP_STATUS_TOO_MANY_REQUESTS) {
          try {
            const usageStatus = await getChatbotUsageStatus();
            updateAssistantMessage(assistantMessage.id, () => buildUsageLimitMessage(usageStatus, t));
          } catch {
            updateAssistantMessage(assistantMessage.id, () =>
              error.message || t(USAGE_LIMIT_REACHED_TRANSLATION_KEY)
            );
          }
        } else {
          updateAssistantMessage(assistantMessage.id, () =>
            error.message || t(ERROR_MESSAGE_TRANSLATION_KEY)
          );
        }
      }
    } finally {
      setIsStreaming(false);
      abortControllerRef.current = null;
    }
  };

  const handleSend = (event: React.FormEvent): void => {
    event.preventDefault();
    const text = input.trim();
    if (!text || isStreaming || isUploadingReferenceImages) return;
    setInput("");
    void sendMessage(text);
  };

  const handleSuggestedPrompt = (prompt: string): void => {
    void sendMessage(prompt);
  };

  const handleReset = (): void => {
    abortControllerRef.current?.abort();
    setInput("");
    setReferenceImages([]);
    setReferenceImageUploadError(null);
    setIsStreaming(false);
    setStreamStatus("thinking");
    setSessionId(null);
    setIsSessionListOpen(false);
    setMessages([createWelcomeMessage()]);
  };

  return {
    abortControllerRef,
    handleSend,
    handleSuggestedPrompt,
    handleReset,
  };
};
